import dayjs, { Dayjs } from "dayjs"

export interface DBTDiaryPageItem {
  displayName: string
  value: number
}

export interface DBTDiaryWeekPageItem {
  displayName: string
  value: string
}

interface CompletedTake {
  completedAt?: string | null
}

const DAILY_WINDOW_DAY_COUNT = 7

/**
 * Returns the inclusive seven-calendar-day diary window for a page offset.
 * Offset zero represents the stable current window; each older page moves back
 * exactly one full window.
 */
export const getDailyDiaryWindow = (
  initialWindowEnd: Dayjs,
  offset: number
): { start: Dayjs; end: Dayjs } => {
  const end = initialWindowEnd.subtract(offset * DAILY_WINDOW_DAY_COUNT, "day")
  return { start: end.subtract(DAILY_WINDOW_DAY_COUNT - 1, "day"), end }
}

/** Returns the inclusive seven-day period ending on a weekly check-in date. */
export const getWeeklyCheckinWindow = (
  completedAt: string
): { start: Dayjs; end: Dayjs } => {
  const end = dayjs(completedAt).startOf("day")
  return { start: end.subtract(DAILY_WINDOW_DAY_COUNT - 1, "day"), end }
}

/** Counts distinct assigned diary dates within an inclusive window.
 * Daily takes store the assigned survey date in completedAt, including backfills.
 */
export const countCompletedTakesInWindow = (
  takes: readonly CompletedTake[],
  window: { start: Dayjs; end: Dayjs }
): number =>
  new Set(
    takes.flatMap((take) => {
      if (!take.completedAt) return []
      const completedDay = dayjs(take.completedAt).startOf("day")
      if (
        !completedDay.isValid() ||
        completedDay.isBefore(window.start, "day") ||
        completedDay.isAfter(window.end, "day")
      ) {
        return []
      }
      return [completedDay.format("YYYY-MM-DD")]
    })
  ).size

/**
 * Returns the seven-day diary period ending on the supplied pager key.
 *
 * The key is a calendar date rather than a timestamp, so construct it from
 * date components instead of relying on date-only string parsing.
 */
export const getDiaryWindowFromEndKey = (
  endKey: string
): { start: Dayjs; end: Dayjs } => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(endKey)
  if (!match) {
    throw new Error(`Invalid diary window end key: ${endKey}`)
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const end = dayjs()
    .year(year)
    .month(month - 1)
    .date(day)
    .startOf("day")

  if (end.year() !== year || end.month() !== month - 1 || end.date() !== day) {
    throw new Error(`Invalid diary window end key: ${endKey}`)
  }

  return { start: end.subtract(DAILY_WINDOW_DAY_COUNT - 1, "day"), end }
}

/** Returns the seven-day offset containing the supplied date. */
export const getDailyDiaryWindowOffset = (
  initialWindowEnd: Dayjs,
  date: string
): number =>
  Math.floor(
    initialWindowEnd.startOf("day").diff(dayjs(date).startOf("day"), "day") /
      DAILY_WINDOW_DAY_COUNT
  )

/** Formats the selected daily diary window for headings and pager labels. */
export const formatDailyDiaryWindow = (window: {
  start: Dayjs
  end: Dayjs
}): string =>
  `${window.start.format("MMMM D")} - ${window.end.format("MMMM D")}`

const getOrdinalSuffix = (dayOfMonth: number): string => {
  if (dayOfMonth >= 11 && dayOfMonth <= 13) {
    return "th"
  }

  switch (dayOfMonth % 10) {
    case 1:
      return "st"
    case 2:
      return "nd"
    case 3:
      return "rd"
    default:
      return "th"
  }
}

/** Formats a completed weekly check-in as an inclusive seven-day period. */
export const formatWeeklyCheckinWindow = (completedAt: string): string => {
  const end = dayjs(completedAt)
  const start = end.subtract(DAILY_WINDOW_DAY_COUNT - 1, "day")
  const startLabel = `${start.format("MMMM")} ${start.date()}${getOrdinalSuffix(
    start.date()
  )}`
  const endLabel =
    start.month() === end.month()
      ? `${end.date()}${getOrdinalSuffix(end.date())}`
      : `${end.format("MMMM")} ${end.date()}${getOrdinalSuffix(end.date())}`

  return `Week of ${startLabel} - ${endLabel}`
}

/**
 * Builds one contiguous navigator from the current period through the oldest
 * period containing a completed weekly check-in. Empty periods between two
 * check-ins are retained so paging always moves in seven-day increments.
 */
export const buildWeekPageItems = (
  initialWindowEnd: Dayjs,
  weeklyCompletionDates: string[]
): DBTDiaryWeekPageItem[] => {
  const currentWindow = getDailyDiaryWindow(initialWindowEnd, 0)
  let oldestOffset = 0

  weeklyCompletionDates.forEach((completedAt) => {
    const completedDay = dayjs(completedAt).startOf("day")
    if (completedDay.isBefore(currentWindow.start, "day")) {
      oldestOffset = Math.max(
        oldestOffset,
        getDailyDiaryWindowOffset(initialWindowEnd, completedAt)
      )
    }
  })

  return Array.from({ length: oldestOffset + 1 }, (_, index) => {
    const offset = oldestOffset - index
    const period = getDailyDiaryWindow(initialWindowEnd, offset)
    return {
      displayName: formatDailyDiaryWindow(period),
      value: period.end.format("YYYY-MM-DD"),
    }
  })
}
